// src/pages/admin/Users.jsx
import React, { useState, useEffect } from 'react';
import { adminApi } from '../../api/adminApi';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Switch,
  FormControlLabel,
  TablePagination,
} from '@mui/material';
import {
  Search,
  MoreVert,
  Edit,
  Delete,
  Block,
  CheckCircle,
  Refresh,
  PersonAdd,
  FilterList,
  LocationOn,
} from '@mui/icons-material';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');

  // Pagination State
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  // Dialog states
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    role: 'customer',
    isActive: true,
  });

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch users with pagination and filters
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({
        page: page + 1, // Backend uses 1-based indexing
        limit: rowsPerPage,
        role: filterRole !== 'all' ? filterRole : undefined,
        search: searchTerm || undefined,
      });
      setUsers(response.data.users || []);
      setTotalCount(response.data.total || 0);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, filterRole]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    if (e.target.value === '' || e.target.value.length > 2) {
      setPage(0);
      fetchUsers();
    }
  };

  const handleFilterChange = (role) => {
    setFilterRole(role);
    setPage(0); // Reset to first page on filter change
  };

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // ============================================================
  // EDIT USER
  // ============================================================
  const handleEditUser = (user) => {
    setEditingUser({
      id: user.id,
      name: user.name,
      email: user.email,
      phone: user.phone,
      role: user.role,
      isActive: user.isActive,
    });
    setOpenEditDialog(true);
    handleMenuClose();
  };

  const handleSaveEditUser = async () => {
    setIsSubmitting(true);
    try {
      await adminApi.updateUser(editingUser.id, {
        name: editingUser.name,
        email: editingUser.email,
        phone: editingUser.phone,
        role: editingUser.role,
        isActive: editingUser.isActive,
      });
      setSuccess('User updated successfully');
      setOpenEditDialog(false);
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // TOGGLE USER STATUS (Inline Action)
  // ============================================================
  const handleToggleStatus = async (user) => {
    try {
      const updatedStatus = !user.isActive;
      await adminApi.updateUser(user.id, { isActive: updatedStatus });
      setSuccess(`User ${user.name || 'User'} ${updatedStatus ? 'activated' : 'deactivated'} successfully`);
      fetchUsers();
      handleMenuClose();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to change user status');
    }
  };

  // ============================================================
  // ADD NEW USER
  // ============================================================
  const handleAddNewUser = () => {
    setNewUserData({
      name: '',
      email: '',
      password: '',
      phone: '',
      role: 'customer',
      isActive: true,
    });
    setOpenAddDialog(true);
  };

  const handleSaveNewUser = async () => {
    if (!newUserData.name || !newUserData.email || !newUserData.password) {
      setError('Name, Email, and Password are required');
      return;
    }

    setIsSubmitting(true);
    try {
      await adminApi.createUser(newUserData);
      setSuccess('User created successfully');
      setOpenAddDialog(false);
      fetchUsers();
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to create user');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================
  // DELETE USER
  // ============================================================
  const handleDeleteUser = async () => {
    if (selectedUser) {
      try {
        await adminApi.deleteUser(selectedUser.id);
        setSuccess('User deleted successfully');
        fetchUsers();
        setDeleteDialog(false);
        setSelectedUser(null);
      } catch (error) {
        setError(error.response?.data?.message || 'Failed to delete user');
      }
    }
  };

  // ============================================================
  // UI HELPERS
  // ============================================================
  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin':
        return 'error';
      case 'vendor':
        return 'primary';
      case 'customer':
        return 'success';
      default:
        return 'default';
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((word) => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const UserStatusChip = ({ isActive }) => (
    <Chip
      icon={isActive ? <CheckCircle /> : <Block />}
      label={isActive ? 'Active' : 'Inactive'}
      size="small"
      color={isActive ? 'success' : 'error'}
      variant="outlined"
    />
  );

  // Helper to count addresses
  const getAddressCount = (user) => {
    if (!user) return 0;
    return user.Addresses?.length || 0;
  };

  if (loading && users.length === 0 && page === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h5" fontWeight="bold">
              Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage platform users and permissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchUsers}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<PersonAdd />}
              onClick={handleAddNewUser}
            >
              Add User
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearch}
            size="small"
            sx={{ flexGrow: 1, minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
          <Stack direction="row" spacing={1}>
            <Chip
              label="All"
              onClick={() => handleFilterChange('all')}
              color={filterRole === 'all' ? 'primary' : 'default'}
              clickable
            />
            <Chip
              label="Admin"
              onClick={() => handleFilterChange('admin')}
              color={filterRole === 'admin' ? 'error' : 'default'}
              clickable
            />
            <Chip
              label="Vendor"
              onClick={() => handleFilterChange('vendor')}
              color={filterRole === 'vendor' ? 'primary' : 'default'}
              clickable
            />
            <Chip
              label="Customer"
              onClick={() => handleFilterChange('customer')}
              color={filterRole === 'customer' ? 'success' : 'default'}
              clickable
            />
          </Stack>
          <IconButton size="small">
            <FilterList />
          </IconButton>
        </Stack>
      </Paper>

      {/* Users Table with Pagination */}
      <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Addresses</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No users found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(user.name)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="600">
                            {user.name || 'Unknown'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {user.id}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {user.email || 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.role || 'customer'}
                        size="small"
                        color={getRoleColor(user.role)}
                        variant="filled"
                      />
                    </TableCell>
                    <TableCell>
                      <UserStatusChip isActive={user.isActive} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        <LocationOn fontSize="small" color="action" />
                        <Typography variant="body2">
                          {getAddressCount(user)}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={(e) => handleMenuOpen(e, user)}>
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Component */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* ============================================================ */}
      {/* ACTION MENU */}
      {/* ============================================================ */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditUser(selectedUser)}>
          <Edit fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => handleToggleStatus(selectedUser)}>
          {selectedUser?.isActive ? (
            <>
              <Block fontSize="small" sx={{ mr: 1 }} /> Deactivate
            </>
          ) : (
            <>
              <CheckCircle fontSize="small" sx={{ mr: 1 }} /> Activate
            </>
          )}
        </MenuItem>
        <MenuItem onClick={() => setDeleteDialog(true)} sx={{ color: 'error.main' }}>
          <Delete fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>

      {/* ============================================================ */}
      {/* ADD NEW USER DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <PersonAdd color="primary" />
            <Typography variant="h6">Add New User</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Full Name"
              value={newUserData.name}
              onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={newUserData.password}
              onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
              required
              helperText="Must be at least 6 characters"
            />
            <TextField
              fullWidth
              label="Phone Number"
              value={newUserData.phone}
              onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={newUserData.role}
                onChange={(e) => setNewUserData({ ...newUserData, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={newUserData.isActive}
                  onChange={(e) => setNewUserData({ ...newUserData, isActive: e.target.checked })}
                  color="success"
                />
              }
              label="Active User"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveNewUser}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* EDIT USER DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Edit color="primary" />
            <Typography variant="h6">Edit User</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={editingUser?.name || ''}
              onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
            />
            <TextField
              fullWidth
              label="Email"
              value={editingUser?.email || ''}
              onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
            />
            <TextField
              fullWidth
              label="Phone"
              value={editingUser?.phone || ''}
              onChange={(e) => setEditingUser({ ...editingUser, phone: e.target.value })}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editingUser?.role || 'customer'}
                onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                label="Role"
              >
                <MenuItem value="admin">Admin</MenuItem>
                <MenuItem value="vendor">Vendor</MenuItem>
                <MenuItem value="customer">Customer</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={editingUser?.isActive !== undefined ? editingUser.isActive : true}
                onChange={(e) => setEditingUser({ ...editingUser, isActive: e.target.value })}
                label="Status"
              >
                <MenuItem value={true}>Active</MenuItem>
                <MenuItem value={false}>Inactive</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveEditUser}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={20} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/* DELETE CONFIRMATION DIALOG */}
      {/* ============================================================ */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        maxWidth="xs"
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "<strong>{selectedUser?.name}</strong>"?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;